import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import authService from '../../services/authService';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { LoadingStatus } from '../../components/ui/LoadingStatus';
import { authLogger } from '../../utils/loggerInstance';
import { useToast } from '../../contexts/ToastContext';

interface SSOCallbackProps {
  provider: 'okta' | 'microsoft';
  callbackPath: string;
}

/**
 * Generic SSO OAuth Callback Handler
 *
 * Handles OAuth callbacks from any SSO provider (Okta, Microsoft, etc.)
 * Extracts authorization code, validates session data, exchanges code for tokens,
 * and completes the authentication flow.
 */
export const SSOCallback: React.FC<SSOCallbackProps> = ({ provider, callbackPath }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  const [loadingMessage, _setLoadingMessage] = useState(
    `Authenticating with ${provider === 'okta' ? 'Okta' : 'Microsoft'}...`,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();
  const { showError } = useToast();

  useEffect(() => {
    // Only process callback if we're actually on the callback route
    if (window.location.pathname !== callbackPath) {
      return;
    }

    // Check if we have the required URL parameters
    const hasCode = !!new URLSearchParams(window.location.search).get('code');
    const hasState = !!new URLSearchParams(window.location.search).get('state');

    if (!hasCode || !hasState) {
      return;
    }

    // Prevent multiple executions of callback handler
    if (callbackProcessed) {
      return;
    }

    const handleCallback = async () => {
      setCallbackProcessed(true);
      try {
        // Extract parameters from URL
        const currentLocationSearch = location.search || window.location.search;
        const currentLocationHash = location.hash || window.location.hash;
        const queryParams = new URLSearchParams(currentLocationSearch);
        const hashFragment = currentLocationHash.substring(1);
        const hashParams = new URLSearchParams(hashFragment);

        authLogger.info(`Processing ${provider} callback`, {
          provider,
          location: location.pathname,
          fullUrl: window.location.href,
        });

        // Check for error in callback
        const errorParam = queryParams.get('error') || hashParams.get('error');
        const errorDescription =
          queryParams.get('error_description') || hashParams.get('error_description');

        if (errorParam) {
          const errorMsg = errorDescription || errorParam || 'Authentication failed';
          authLogger.error(`${provider} callback error`, {
            provider,
            error: errorParam,
            description: errorDescription,
          });
          setError(errorMsg);
          setLoading(false);
          showError(errorMsg);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Extract authorization code and state
        const code = queryParams.get('code') || hashParams.get('code');
        const state = queryParams.get('state') || hashParams.get('state');

        if (!code) {
          throw new Error(`No authorization code found in ${provider} callback`);
        }

        if (!state) {
          authLogger.warn(`No state parameter in ${provider} callback`);
        }

        // Call provider-specific callback handler
        let userInfo;
        if (provider === 'okta') {
          userInfo = await authService.handleOktaCallback(code);
        } else if (provider === 'microsoft') {
          userInfo = await authService.handleMicrosoftCallback(code);
        } else {
          throw new Error(`Unknown provider: ${provider}`);
        }

        authLogger.info(`${provider} authentication successful`, {
          provider,
          userId: userInfo.user?.id,
          email: userInfo.user?.email,
        });

        // Refresh auth context
        await refreshAuth();

        // Redirect to chat
        navigate('/chat', { replace: true });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        authLogger.error(`${provider} callback error`, { provider, error: errorMessage });
        setError(errorMessage);
        setLoading(false);
        showError(errorMessage);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [location, callbackProcessed, navigate, refreshAuth, showError, provider, callbackPath]);

  if (!loading && error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <h2 className='text-2xl font-bold text-red-600 mb-4'>Authentication Failed</h2>
          <p className='text-gray-700 mb-4'>{error}</p>
          <p className='text-sm text-gray-500'>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center justify-center min-h-screen'>
      <div className='text-center'>
        <LoadingSpinner size='lg' />
        <LoadingStatus message={loadingMessage} />
      </div>
    </div>
  );
};
